import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Crown, Sparkles, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';

export function UpgradeModal({ open, onClose, feature, requiredPlan = 'pro' }) {
  const navigate = useNavigate();
  if (!open) return null;

  const planName = requiredPlan === 'enterprise' ? 'Enterprise' : 'Pro';
  const planColor = requiredPlan === 'enterprise' ? '#3B82F6' : '#0D9488';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={onClose}
          data-testid="upgrade-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl"
            style={{ background: 'var(--t-bg-card-solid)', border: '1px solid var(--t-border)' }}
            data-testid="upgrade-modal"
          >
            {/* Header */}
            <div className="relative p-6 pb-4" style={{ background: `linear-gradient(135deg, ${planColor}15, ${planColor}05)` }}>
              <button onClick={onClose}
                className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-muted)' }}
                data-testid="upgrade-modal-close">
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ background: `${planColor}20` }}>
                <Lock className="w-5 h-5" style={{ color: planColor }} />
              </div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>
                Upgrade to {planName}
              </h3>
              <p className="text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>
                {feature || `This feature requires a ${planName} plan.`}
              </p>
            </div>

            {/* Benefits */}
            <div className="px-6 py-4 space-y-2.5">
              {[
                { icon: Sparkles, text: 'Unlimited career sessions & assessments' },
                { icon: Crown, text: 'Smart coaching, hints & career reports' },
                { icon: Lock, text: 'All career tools & community access' },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: `${planColor}15` }}>
                    <b.icon className="w-3 h-3" style={{ color: planColor }} />
                  </div>
                  <span className="text-[13px] font-medium" style={{ color: 'var(--t-text-body)' }}>{b.text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="px-6 pb-6 pt-2 space-y-2">
              <Button
                className="w-full h-11 font-bold text-sm rounded-lg"
                style={{ background: planColor, color: '#fff' }}
                onClick={() => { onClose(); navigate('/subscription'); }}
                data-testid="upgrade-modal-cta"
              >
                View Plans <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button onClick={onClose}
                className="w-full text-center text-xs py-2 font-medium transition-colors"
                style={{ color: 'var(--t-text-muted)' }}
                data-testid="upgrade-modal-dismiss">
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Inline upgrade badge that replaces a feature's CTA when locked.
 */
export function UpgradeBadge({ feature, requiredPlan = 'pro', onClick, compact = false }) {
  const planName = requiredPlan === 'enterprise' ? 'Enterprise' : 'Pro';
  const planColor = requiredPlan === 'enterprise' ? '#3B82F6' : '#0D9488';

  if (compact) {
    return (
      <button onClick={onClick}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold transition-all hover:scale-105"
        style={{ background: `${planColor}15`, color: planColor }}
        data-testid={`upgrade-badge-${feature}`}>
        <Crown className="w-2.5 h-2.5" /> {planName}
      </button>
    );
  }

  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02]"
      style={{ background: `${planColor}10`, border: `1px solid ${planColor}20`, color: planColor }}
      data-testid={`upgrade-badge-${feature}`}>
      <Lock className="w-3.5 h-3.5" />
      <span>Upgrade to {planName} to unlock</span>
      <ArrowRight className="w-3 h-3" />
    </button>
  );
}
